import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Checkbox, Paper, Slide, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, TableSortLabel, Typography
} from "@mui/material";
import { columns } from "../../../dtos/columns";
import { SortOrder } from "./serverSideTable";
import { visuallyHidden } from '@mui/utils';
import MButton from "../../inputs/yup/materialui/mButton";

interface TablaProps<T> {
    url: string;
    columns: columns<T>[];
    titulo: React.ReactNode;
    esMultiple: boolean;
    conditionalRowStyles?: any;
    onRowClicked?: (row: any) => void;
    control: controlProps<T>;
    callApi?: boolean;
    setFilaSeleccionada: React.Dispatch<React.SetStateAction<any>>;
    onHide?: () => void;
    toggleFilterButtonClicked?: boolean;
    messageSingular?: string;
    messagePlural?: string;
    esCheckDisponible?: boolean;
}
export interface controlProps<T> {
    data: never[]
    loading: boolean
    totalRows: number
    perPage: number
    page: number
    order: SortOrder
    params: URLSearchParams
    fetchData: (pageParam?: number) => Promise<void>
    getData: (url: string) => Promise<void>
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    setPage: React.Dispatch<React.SetStateAction<number>>
    setOrder: React.Dispatch<React.SetStateAction<SortOrder>>
    setPerPage: React.Dispatch<React.SetStateAction<number>>
    esError?: boolean
}

const Tabla = <T,>({
    url,
    columns,
    titulo,
    control,
    callApi = true,
    setFilaSeleccionada,
    onHide,
    esMultiple,
    esCheckDisponible = true
}: TablaProps<T>) => {

    useEffect(() => {
        if (callApi)
            control.fetchData()
    }, [])

    const handleChangePage = (event: any, page: number) => {
        control.fetchData(page)
    };

    const handleChangeRowsPerPage = async (event: any) => {

        control.setLoading(true)
        control.setPage(0);

        const newPerPage = parseInt(event.target.value, 10);
        const skip = control.perPage * (control.page - 0)

        control.params.delete('skip')
        control.params.append('skip', skip.toString())

        control.params.delete('take')
        control.params.append('take', newPerPage.toString())

        if (control.params.toString()) {
            url += '?' + control.params.toString()
        }

        control.setPerPage(newPerPage);

        await control.getData(url)
    };

    const createSortHandler = (property: any) => (event: React.MouseEvent<unknown>) => {
        handleRequestSort(event, property);
    };

    const handleRequestSort = async (
        event: React.MouseEvent<unknown>,
        property: any,
    ) => {
        const isAsc = orderBy === property && order === "asc";
        const newOrder = isAsc ? "desc" : "asc"
        setOrder(newOrder);
        setOrderBy(property);

        control.setOrder(newOrder);
        control.params.delete('sort');
        control.params.append('sort', property);

        control.params.delete('order')
        control.params.append('order', control.order);

        control.params.delete('skip')
        control.params.append('skip', '0')

        control.params.delete('take')
        control.params.append('take', control.perPage.toString())

        if (control.params.toString()) {
            const REP = "REP";
            const params = control.params.toString().replace("asc", REP).replace("desc", REP).replace(REP, newOrder)
            url += '?' + params;
        }

        await control.getData(url)
    };

    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

    const getRowId = (row: any) => {
        return (row as any).id || JSON.stringify(row);
    };

    const toggleRowSelection = (row: any) => {
        const rowId = getRowId(row); // Usa el método seguro para ID
        if (esMultiple) {
            setSelectedRows(prev => {
                const newSet = new Set(prev);
                if (newSet.has(rowId)) {
                    newSet.delete(rowId);
                } else {
                    newSet.add(rowId);
                }

                const selectedData = control.data.filter(r => newSet.has(getRowId(r)));

                // ✅ evita llamar directamente en render
                setTimeout(() => setFilaSeleccionada(selectedData), 0);
                return newSet;
            });
        } else {
            setSelectedRows(new Set([rowId]));
            // ✅ evita llamada directa
            setTimeout(() => setFilaSeleccionada(row), 0);
            // if (onHide) onHide();
        }
    };

    const toggleSelectAll = () => {
        if (esMultiple) {
            if (selectedRows.size === control.data.length) {
                setSelectedRows(new Set());
                setFilaSeleccionada([]);
            } else {
                const allIds = control.data.map(r => getRowId(r));
                setSelectedRows(new Set(allIds));
                setFilaSeleccionada([...control.data]);
            }
        }
    };

    //Constants to manage order
    const [order, setOrder] = useState<"asc" | "desc">("asc");
    const [orderBy, setOrderBy] = useState<keyof T | null>(null);

    const sortedData = useMemo(() => {
        if (!orderBy) return control.data;
        return [...control.data].sort((a, b) => {
            const valueA = a[orderBy] as any;
            const valueB = b[orderBy] as any;
            return (valueA < valueB ? -1 : 1) * (order === "asc" ? 1 : -1);
        });
    }, [control.data, orderBy, order]);

    useEffect(() => {
        setFilaSeleccionada([]);
        setSelectedRows(new Set());
    }, [control.data])

    return (
        <Paper>
            {titulo}
            <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
                <Table sx={{ tableLayout: "auto", width: "100%" }}>
                    <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableRow>
                            {esCheckDisponible ?
                                <TableCell padding="checkbox">
                                    {esMultiple && (
                                        <Checkbox
                                            indeterminate={selectedRows.size > 0 && selectedRows.size < control.data.length}
                                            checked={selectedRows.size === control.data.length && control.data.length > 0}
                                            onChange={toggleSelectAll}
                                        />
                                    )}
                                </TableCell> : <></>
                            }
                            {columns.map((column: columns<T>) => (
                                !column.omit && (
                                    <TableCell
                                        key={column.name as string}
                                        sortDirection={control.params.get('sort') === column.sortField ? control.order : false}
                                        align={column.alling || 'left'}
                                        width={column.width || "auto"}
                                        sx={{
                                            minWidth: column.width || 120,
                                            paddingX: 2,
                                            paddingY: 2,
                                        }}
                                    >
                                        {column.sortable ? (
                                            <TableSortLabel
                                                active={control.params.get('sort') === column.sortField}
                                                direction={control.params.get('sort') === column.sortField ? control.order : 'asc'}
                                                onClick={createSortHandler(column.sortField)}
                                            >
                                                {column.name}
                                                {control.params.get('sort') === column.sortField ? (
                                                    <Box component="span" sx={visuallyHidden}>
                                                        {control.order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                    </Box>
                                                ) : null}
                                            </TableSortLabel>
                                        ) : (
                                            column.name
                                        )}
                                    </TableCell>
                                )
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1} align="center">
                                    No se ha encontrado data
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedData.map((row: T) => (
                                <TableRow
                                    key={getRowId(row)}
                                    hover
                                    selected={selectedRows.has(getRowId(row))}
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => toggleRowSelection(row)}
                                >
                                    {esCheckDisponible ?
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedRows.has(getRowId(row))}
                                                onChange={() => toggleRowSelection(row)}
                                                onClick={() => toggleRowSelection(row)}
                                                sx={{ cursor: "pointer" }}
                                            />
                                        </TableCell> : <></>
                                    }
                                    {columns.map((column) =>
                                        !column.omit ? (
                                            <TableCell
                                                key={column.name as string}
                                                align={column.alling || "left"}
                                                sx={{
                                                    whiteSpace: column.wrap ? "normal" : "nowrap",
                                                    minWidth: column.width || 120,
                                                    paddingX: 2,
                                                    paddingY: 2,
                                                }}
                                            >
                                                {column.cell ? column.cell(row) : column.selector(row)}
                                            </TableCell>
                                        ) : null
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: 'Todos', value: control.totalRows }]}
                component="div"
                count={control.totalRows}
                rowsPerPage={control.perPage}
                page={control.page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={<Typography variant="body2" sx={{ mt: 0 }}>Filas por página</Typography>}
                labelDisplayedRows={({ from, to, count }: { from: number; to: number; count: number }) =>
                    `${from}-${to} de ${count}`
                } slotProps={{
                    displayedRows: {
                        sx: { mt: 2 }
                    }
                }}
            />
        </Paper>
    );
};

export default Tabla;
